"use client";

import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/components/cart/CartProvider";
import { fetchTenantSettings } from "@/lib/api";
import { isModuleEnabled } from "@/lib/modules";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/types/product";
import type { TenantSettingsDto } from "@/types/tenant";

export default function CartPage() {
  const { cart, isLoading, error, updateQuantity, removeItem, clear } = useCart();
  const router = useRouter();
  const [tenant, setTenant] = useState<TenantSettingsDto | null>(null);

  useEffect(() => {
    fetchTenantSettings().then(setTenant).catch(() => setTenant(null));
  }, []);

  if (tenant && !isModuleEnabled(tenant.settings, "products")) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10 md:px-8">
        <p role="alert" className="text-slate-600">The store is not available.</p>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10 md:px-8">
        <h1 className="text-3xl font-bold">Your cart</h1>
        <div className="mt-8 space-y-4">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-24 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      </main>
    );
  }

  if (error || !cart) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10 md:px-8">
        <h1 className="text-3xl font-bold">Your cart</h1>
        <p className="mt-4 text-red-600" role="alert">
          {error || "Could not load your cart."}
        </p>
      </main>
    );
  }

  if (cart.items.length === 0) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-16 text-center md:px-8">
        <ShoppingBag className="mx-auto h-12 w-12 text-slate-300" aria-hidden />
        <h1 className="mt-4 text-3xl font-bold">Your cart is empty</h1>
        <Link href="/account" className="mt-4 inline-flex min-h-11 items-center underline">Your orders and saved carts</Link>
        <p className="mt-2 text-slate-600">Add some products to get started.</p>
        <Link
          href="/products"
          className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded bg-[var(--color-primary)] px-5 py-2 font-medium text-white"
        >
          Browse products <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 md:px-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Your cart</h1>
        <Button type="button" variant="outline" onClick={() => clear()}>
          Clear cart
        </Button>
      </div>
      <Link href="/account" className="mb-5 inline-flex min-h-11 items-center underline">Save this cart or view your orders</Link>

      <ul className="divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
        {cart.items.map((item) => (
          <li key={item.id} className="grid gap-4 py-5 sm:grid-cols-[auto_1fr_auto]">
            <div className="relative h-24 w-24 overflow-hidden bg-[var(--color-surface-muted)]">
              {item.productPhotoUrl ? (
                <Image
                  src={item.productPhotoUrl}
                  alt={item.productName || "Product"}
                  fill
                  sizes="96px"
                  unoptimized
                  className="object-contain p-2"
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <ShoppingBag className="h-8 w-8 text-slate-300" aria-hidden />
                </div>
              )}
            </div>
            <div>
              <Link
                href={item.productSlug ? `/products/${item.productSlug}` : "#"}
                className="font-semibold hover:text-[var(--color-primary)]"
              >
                {item.productName || "Unknown product"}
              </Link>
              <p className="mt-1 text-sm text-slate-500">
                {formatPrice(item.unitPrice, null, tenant?.settings.currency)} each
              </p>
              <div className="mt-3 inline-flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Decrease quantity"
                  onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                  disabled={item.quantity <= 1}
                >
                  <Minus className="h-4 w-4" aria-hidden />
                </Button>
                <span className="min-w-[2ch] text-center font-medium">{item.quantity}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Increase quantity"
                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
            <div className="flex flex-col items-end justify-between gap-3">
              <span className="font-semibold">{formatPrice(item.lineTotal, null, tenant?.settings.currency)}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove item"
                onClick={() => removeItem(item.id)}
              >
                <Trash2 className="h-4 w-4 text-red-600" aria-hidden />
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-col items-end gap-4">
        <div className="text-lg">
          Subtotal: <span className="font-bold">{formatPrice(cart.subtotal, null, tenant?.settings.currency)}</span>
        </div>
        <Button
          type="button"
          size="lg"
          onClick={() => router.push("/checkout")}
        >
          Proceed to checkout <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
        </Button>
      </div>
    </main>
  );
}
